import React, { useCallback, useMemo, useState } from 'react';

import { BugIcon, PeopleIcon } from '@primer/octicons-react';
import { StyledOcticon } from '@primer/react';

import BasicDropdown from '../../../common/BasicDropdown/BasicDropdown';
import Button from '../../../common/Button/Button';
import CardView from '../../../common/CardView/CardView';
import ErrorSlate from '../../../ErrorSlate/ErrorSlate';
import LoadingSpinner from '../../../common/LoadingSpinner/LoadingSpinner';
import PaginationRow from '../../../common/PaginationRow/PaginationRow';
import SearchInput from '../../../common/TextInputs/SearchInput/SearchInput';

import AdminBulkUserRemovalModal from '../AdminUserRemovalModal/AdminBulkUserRemovalModal';
import AdminCreateUserModal from '../AdminCreateUserModal/AdminCreateUserModal';
import AdminEditUserModal from '../AdminEditUserModal/AdminEditUserModal';
import FilterCountButton from '../../FilterCountButton';
import AdminUserRemovalModal from '../AdminUserRemovalModal/AdminUserRemovalModal';
import ResetPasswordDialog from '../AdminResetPasswordDialog/ResetPasswordDialog';
import UserAdminTable from '../UserAdminTable/UserAdminTable';
import UserSessionsModal from '../UserSessionsModal/UserSessionsModal';

import UserTableFilterModal, { ROLE, USER_STATUS as STATUS } from '../UserTableFilterModal/UserTableFilterModal';
import UserActivationModal from '../UserActivationModal/UserActivationModal';

import useUserContext from '../../../contexts/UserData/UserData';

import changeUserStatus from '../../../../utils/api_functions/changeUserStatus';
import createUser from '../../../../utils/api_functions/adminUserCreation';
import editUser from '../../../../utils/api_functions/editUser';
import removeUser from '../../../../utils/api_functions/removeKeycloakUser';
import removeUsers from '../../../../utils/api_functions/removeMultipleUsers';
import resetUserPassword from '../../../../utils/api_functions/resetUserPassword';
import classNames from 'classnames';

const ACTION_REMOVE_USERS = 'removeUsers';
const BULK_ACTIONS = [
  {
    id: ACTION_REMOVE_USERS,
    label: 'Delete',
  }
]

const USER_ADMIN_MODAL = {
  CREATE_USER: 'create_user',
  EDIT_USER: 'edit_user',
  RESET_PASSWORD: 'reset_password',
  ACTIVATE_USER: 'activate_user',
  DELETE_USER: 'delete_user',
  BULK_DELETE: 'bulk_delete',
  FILTER_USERS: 'filter_users',
  USER_SESSIONS: 'user_sessions',
}

const BugGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={BugIcon}
    size={64}
  />
)

const NoUsersGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={PeopleIcon}
    size={64}
  />
)

const countActiveUserFilters = (filterObj = {}) => {
  let filterCount = 0;
  if (filterObj.role !== ROLE.All) {
    filterCount += 1;
  }
  if (filterObj.status !== STATUS.All) {
    filterCount += 1;
  }

  return filterCount;
}

// TODO - Make sure Search, Filter, and Selection States all work well together
// Test changing search terms, filter states, and selection states interchangably
// Particularly - Test what happens to selected items if the search term filters them out
// - Discuss this with product

export const UserAdminTab = ({
  error = null,
  loading = false,
  users = [],
  totalUserCount = 0,
  searchQuery = '',
  filterObj = { role: ROLE.All, status: STATUS.All },
  setSearchQuery = () => {},
  setFilterObj = () => {},
}) => {
  const [isSelectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedPage, setSelectedPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(25);

  const [selectedTableAction, setSelectedTableAction] = useState(null);

  const [showModal, setShowModal] = useState(null);
  // Either ID or User object as needed
  const [focusedUser, setFocusedUser] = useState(null)
  const [modalError, setModalError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleBulkActionApply = useCallback(() => {
    if (selectedTableAction === ACTION_REMOVE_USERS) {
      setShowModal(USER_ADMIN_MODAL.BULK_DELETE);
    }
  }, [selectedTableAction])

  const handleBulkActionCancel = () => {
    setSelectedTableAction(null);
    setSelectedUserIds([])
    setSelectAllChecked(false);
  }

  const handleSearchInputChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, [setSearchQuery])

  const handleUserCreation = (
    email, password, isAdmin, isTemporary,
  ) => {
    setModalError(null);
    setActionLoading(true);
    createUser({
      email, password, isTemporary, isAdmin,
    }).then(() => {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);
      setActionLoading(false);
    }).catch(() => {
      setActionLoading(false);
      setModalError('An unexpected error occurred while trying to create that user. Please try again in a few minutes.')
    })
  }

  const handleUserEdit = (userData) => {
    setModalError(null);
    setActionLoading(true);
    editUser(userData).then(() => {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);
      setActionLoading(false);
    }).catch(() => {
      setActionLoading(false);
      setModalError('An unexpected error occurred while trying to edit the selected user. Please try again in a few minutes.')
    })
  }

  const handleUserActivation = (userId, activateUser) => {
    setModalError(null);
    setActionLoading(true);
    changeUserStatus(userId, activateUser).then(() => {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);
      setActionLoading(false);
    }).catch(() => {
      setActionLoading(false);
      setModalError('An unexpected error occurred while trying to update the selected user. Please try again in a few minutes.')
    })
  }

  const handleUserRemoval = (userId) => {
    setModalError(null);
    setActionLoading(true);
    removeUser(userId).then(() => {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);
      setSelectedTableAction(null);
      setSelectedUserIds([]);
      setSelectAllChecked(false);
      setActionLoading(false);
    }).catch((e) => {
      setActionLoading(false);
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected user. Please try again in a few minutes.')
      }
    })
  }

  const handleBulkUserRemoval = (userIds) => {
    setModalError(null);
    setActionLoading(true);
    removeUsers(userIds).then(() => {
      setShowModal(null);
      setModalError(null);
      setFocusedUser(null);
      setSelectedTableAction(null);
      setSelectAllChecked(false);
      setSelectedUserIds([]);
      setActionLoading(false);
    }).catch((e) => {
      setActionLoading(true);
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to remove the selected user(s). Please try again in a few minutes.')
      }
    })
  }

  const handlePasswordReset = (userId, password, isTemporary) => {
    setModalError(null);
    setActionLoading(true);
    resetUserPassword(userId, password, isTemporary).then((e) => {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);
      setActionLoading(false);
    }).catch((e) => {
      setActionLoading(false);
      if (e?.msg) {
        setModalError(e.msg);
      } else {
        setModalError('An unexpected error occurred while trying to reset send the password reset link. Please try again in a few minutes.')
      }
    })
  }

  const viewUserSessions = (userId) => {
    setShowModal(USER_ADMIN_MODAL.USER_SESSIONS);
    setFocusedUser(userId);
  }

  const onShowUserActivationModal = (user) => {
    if (!user?.enabled) {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);

      handleUserActivation(user.id, true);
    } else {
      setShowModal(USER_ADMIN_MODAL.ACTIVATE_USER);
      setFocusedUser(user);
      setModalError(null);
    }
  }

  const onShowUserRemovalModal = (userId) => {
    setShowModal(USER_ADMIN_MODAL.DELETE_USER)
    setFocusedUser(userId);
    setModalError(null);
  }

  const onShowPasswordResetModal = (userId) => {
    setShowModal(USER_ADMIN_MODAL.RESET_PASSWORD);
    setFocusedUser(userId);
    setModalError(null);
  }

  const onModalToggle = (user) => {
    if (!user) {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);
    }
  }

  const onModalClose = () => {
    setShowModal(null);
    setFocusedUser(null);
    setModalError(null);
  }

  const onBulkUserRemovalDialogToggle = (selectedUsers) => {
    if (!selectedUsers) {
      setShowModal(null);
      setFocusedUser(null);
      setModalError(null);
    }
  }

  const onFilterConfirmed = (filter) => {
    setFilterObj(filter);
    setShowModal(null);
    setModalError(null);
  }

  const onFilterClear = () => {
    setFilterObj({
      role: 'all',
      status: 'all',
    })
    setShowModal(null);
    setModalError(null);
  }

  const handleUserClick = useCallback((user) => {
    setShowModal(USER_ADMIN_MODAL.EDIT_USER);
    setFocusedUser(user?.original);
    setModalError(null);
  }, [])

  const formatResultsCount = useCallback(val => `${val}/${totalUserCount} results`, [totalUserCount]);

  const sessionEmail = useMemo(() => {
    if (showModal === USER_ADMIN_MODAL.USER_SESSIONS) {
      const user = users.find(({ id }) => id === focusedUser);
      return user?.email;
    }

    return '';
  }, [focusedUser, showModal, users])

  const errorText = error ? 'An unexpected error occurred while trying to load the user list.' : 'No users found.';

  // Hides the CTA section if there are simply no users to display
  const errorProps = error ? {} : { CTAInfo: null }

  return (
    <div>
      <div className="flex items-center justify-start mb-2">
        <div className="flex items-center justify-start mx-2">
          <BasicDropdown
            direction="bottom-left"
            dropdownLabel={selectedTableAction ? '' : 'Bulk Actions'}
            values={BULK_ACTIONS}
            selectedValue={selectedTableAction}
            onValueSelected={setSelectedTableAction}
          />
          {
            selectedTableAction && (
              <>
                <Button className="mx-1" size="large" variant="danger" onClick={handleBulkActionApply} disabled={!selectedUserIds?.length}>
                  {
                    selectedUserIds?.length ? `Apply (${selectedUserIds.length})` : 'Apply'
                  }
                </Button>
                <Button size="large" onClick={handleBulkActionCancel}>
                  Cancel
                </Button>
              </>
            )
          }
        </div>
        <SearchInput className="mx-2" value={searchQuery} onChange={handleSearchInputChange} block />
        <FilterCountButton activeFilters={countActiveUserFilters(filterObj)} onClick={() => setShowModal(USER_ADMIN_MODAL.FILTER_USERS)}>Filter</FilterCountButton>
        <Button className="mx-2" variant="primary" onClick={() => setShowModal(USER_ADMIN_MODAL.CREATE_USER)}>Create Users</Button>
      </div>
      <div className="relative">
        {
          (loading || actionLoading) && (
            <LoadingSpinner
              animationKey="user-admin-table-loading-spinner"
              loadingText="Loading..."
              useOverlay
            />
          )
        }
        <div className={classNames('border shadow', {
          'hidden': !users?.length || error,
        })}>
          <UserAdminTable
            users={users}
            selectedUserIds={selectedUserIds}
            setSelectedUserIds={setSelectedUserIds}
            showUserActivationModal={onShowUserActivationModal}
            viewUserSessions={viewUserSessions}
            removeUser={onShowUserRemovalModal}
            onUserClick={handleUserClick}
            hideCheckboxes={!selectedTableAction}
            isSelectionColumnChecked={isSelectAllChecked}
            setSelectionColumnChecked={setSelectAllChecked}
            selectedPage={selectedPage}
            resultsPerPage={resultsPerPage}
          />
        </div>
        {
          (!users?.length || error) && (
            <CardView className="p-4" disableFullHeight>
              <ErrorSlate
                errorText={errorText}
                subText={null}
                Graphic={error ? BugGraphic : NoUsersGraphic}
                {...errorProps}
              />
            </CardView>
          )
        }
      </div>
      <PaginationRow
        className="p-4"
        numberOfResults={users?.length}
        selectedPage={selectedPage}
        resultsPerPage={resultsPerPage}
        setSelectedPage={setSelectedPage}
        setResultsPerPage={setResultsPerPage}
        formatResultsCount={formatResultsCount}
      />
      {
        showModal === USER_ADMIN_MODAL.CREATE_USER && (
          <AdminCreateUserModal
            error={modalError}
            showDialog={showModal === USER_ADMIN_MODAL.CREATE_USER}
            onDialogToggle={onModalToggle}
            onUserCreated={handleUserCreation}
          />
        )
      }
      {
        showModal === USER_ADMIN_MODAL.FILTER_USERS && (
          <UserTableFilterModal
            filter={filterObj}
            showDialog={showModal === USER_ADMIN_MODAL.FILTER_USERS}
            onDialogToggle={onModalToggle}
            onFilterConfirm={onFilterConfirmed}
            onClear={onFilterClear}
          />
        )
      }
      {
        showModal === USER_ADMIN_MODAL.EDIT_USER && (
          <AdminEditUserModal
            error={modalError}
            user={focusedUser}
            userId={focusedUser?.id}
            showDialog={showModal === USER_ADMIN_MODAL.EDIT_USER}
            onDialogToggle={onModalToggle}
            onUserEdited={handleUserEdit}
            onUserDeleted={() => onShowUserRemovalModal(focusedUser?.id)}
            onPasswordReset={onShowPasswordResetModal}
          />
        )
      }
      {
        showModal === USER_ADMIN_MODAL.USER_SESSIONS && (
          <UserSessionsModal
            userEmail={sessionEmail}
            userId={focusedUser}
            showDialog={showModal === USER_ADMIN_MODAL.USER_SESSIONS}
            onClose={onModalClose}
          />
        )
      }
      {
        showModal === USER_ADMIN_MODAL.ACTIVATE_USER && (
          <UserActivationModal
            error={modalError}
            user={focusedUser}
            showDialog={showModal === USER_ADMIN_MODAL.ACTIVATE_USER}
            onDialogToggle={onModalToggle}
            onUserUpdated={handleUserActivation}
          />
        )
      }
      {
        showModal === USER_ADMIN_MODAL.DELETE_USER && (
          <AdminUserRemovalModal
            error={modalError}
            userId={focusedUser}
            showDialog={showModal === USER_ADMIN_MODAL.DELETE_USER}
            onUserRemoved={handleUserRemoval}
            onDialogToggle={onModalToggle}
          />
        )
      }
      {
        showModal === USER_ADMIN_MODAL.BULK_DELETE && (
          <AdminBulkUserRemovalModal
            error={modalError}
            userIds={selectedUserIds}
            showDialog={showModal === USER_ADMIN_MODAL.BULK_DELETE}
            onUsersRemoved={handleBulkUserRemoval}
            onDialogToggle={onBulkUserRemovalDialogToggle}
          />
        )
      }
      {
        showModal === USER_ADMIN_MODAL.RESET_PASSWORD && (
          <ResetPasswordDialog
            error={modalError}
            userId={focusedUser}
            showDialog={showModal === USER_ADMIN_MODAL.RESET_PASSWORD}
            onPasswordReset={handlePasswordReset}
            onDialogToggle={onModalToggle}
          />
        )
      }
    </div>
  )
}

const handleSearch = (users, searchQuery, filterObj) => users.filter((user) => {
  const searchTerm = searchQuery?.toLowerCase() || '';

  // Don't show the default admin user
  if (user?.username === 'admin') {
    return false;
  }

  // Filters based on the role
  if (user?.realmRoles?.includes(ROLE.Admin)) {
    if (filterObj.role === ROLE.User) {
      return false;
    }
  } else if (filterObj.role === ROLE.Admin) {
    return false;
  }

  // Filters based on the status
  if (user?.enabled) {
    if (filterObj.status === STATUS.Inactive) {
      return false;
    }
  } else if (filterObj.status === STATUS.Active) {
    return false;
  }
  return user?.email.toLowerCase().includes(searchTerm)
          || user?.firstName?.toLowerCase().includes(searchTerm)
          || user?.lastName?.toLowerCase().includes(searchTerm);
})

const ContextualUserAdminTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterObj, setFilterObj] = useState({
    role: ROLE.All,
    status: STATUS.All,
  })

  const { usersLoading, users, usersError } = useUserContext();

  const filteredUsers = useMemo(() => handleSearch(users, searchQuery, filterObj), [filterObj, searchQuery, users]);

  const usersWithoutAdmin = users?.filter(user => user?.username !== 'admin');
  const totalUserCount = usersWithoutAdmin?.length || 0;
  return (
    <UserAdminTab
      error={usersError}
      loading={usersLoading}
      users={filteredUsers}
      totalUserCount={totalUserCount}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filterObj={filterObj}
      setFilterObj={setFilterObj}
    />
  )
}

export default ContextualUserAdminTab;
